// In-progress session state — accumulates the manifest fields (Section 5)
// as the participant moves through PRE_SURVEY → ... → M2C2_BATTERY, then
// SessionCompleteScreen assembles + persists the final manifest object.
// This store is intentionally NOT persisted to IndexedDB directly — it's
// scratch space for one session; only the finished manifest gets written
// to pending_uploads.

import { create } from 'zustand';

const blank = () => ({
  sessionId: null,
  participantId: null,
  sessionNumber: null,
  contentIndex: null,
  dayNumber: null,
  sessionWithinDay: null,
  startTsUtc: null,

  affectPre: null,
  affectPost: null,

  textTrial: null, // { trial_id, content_key, passage_text, cue_onset_ts_utc, recording_start_ts_utc, recording_end_ts_utc, duration_ms, blob, was_abandoned }
  imageTrial: null,
  deviceInfo: null, // captured once, from the first recorder (Section 5 device_info is session-level)

  m2c2Results: null,

  abandoned: false,
  abandonmentPhase: null,
});

export const useSessionStore = create((set, get) => ({
  ...blank(),

  startSession({ sessionId, participantId, sessionNumber, contentIndex, dayNumber, sessionWithinDay }) {
    set({
      ...blank(),
      sessionId,
      participantId,
      sessionNumber,
      contentIndex,
      dayNumber,
      sessionWithinDay,
      startTsUtc: new Date().toISOString(),
    });
  },

  setAffectPre(valence, arousal) {
    set({ affectPre: { valence, arousal, recorded_at_utc: new Date().toISOString() } });
  },

  setAffectPost(valence, arousal) {
    set({ affectPost: { valence, arousal, recorded_at_utc: new Date().toISOString() } });
  },

  setTextTrial(trial) {
    set({ textTrial: trial });
  },

  setDeviceInfo(info) {
    set({ deviceInfo: info });
  },

  setImageTrial(trial) {
    set({ imageTrial: trial });
  },

  setM2C2Results(results) {
    set({ m2c2Results: results });
  },

  abandon(phase) {
    set({ abandoned: true, abandonmentPhase: phase });
  },

  // Reinstate a session that was parked in IndexedDB while the M2C2 battery
  // ran as separate page loads (see utils/m2c2Handoff.js). Navigating away
  // destroys this store, so the snapshot — audio Blobs included — is read
  // back and dropped in wholesale on return.
  restore(snapshot) {
    set({ ...blank(), ...snapshot });
  },

  reset() {
    set(blank());
  },

  getSnapshot() {
    return { ...get() };
  },
}));
